import React, { useRef, useState } from 'react';
import {
  Box,
  ButtonWithAction,
  Checkbox,
  Cluster,
  Text,
  constants,
  PencilIcon,
} from '@thecb/components';
import styled from 'styled-components';
import { noop } from 'rxjs';
import { useSelector } from 'react-redux';
import {
  formatCurrency,
  formatWithParentheses,
  removeLeadingMinus,
} from '../../utils/helpers';

export const SearchResultItem = ({
  item,
  checked,
  onCheckBoxClick = noop,
  handleAmountUpdate = noop,
  handleAnnotationUpdate = noop,
  annotationFields = [],
}) => {
  const originalItemAmt = useRef(item.amount); // cached for comparison by isAmountValid -> the searchResults redux state changes on submit and handles which amount is added to the cart
  const [isEditing, setIsEditing] = useState(false);
  const [inlineAmount, setInlineAmount] = useState(item.amount);
  const [inlineAmountError, setInlineAmountError] = useState(false);
  const {
    currentProduct: {
      product_data: { accepts_overpayment, accepts_underpayment },
    },
  } = useSelector((state) => state.products);

  const isAmountValid = (amount) => {
    const numericAmount = Number(amount);

    if (
      isNaN(numericAmount) ||
      amount === '' ||
      amount === null ||
      amount === undefined ||
      numericAmount === 0
    ) {
      return false;
    }

    if (numericAmount < 0 && !item.discount_allowed) {
      return false;
    } else if (
      numericAmount > originalItemAmt.current &&
      !accepts_overpayment
    ) {
      return false;
    } else if (
      numericAmount < originalItemAmt.current &&
      !accepts_underpayment
    ) {
      return false;
    } else {
      return true;
    }
  };

  const renderEditAmountButton = () =>
    /* if both accepts over/under payment are false, we don't need to show the button at all because the user can't modify the data with the edit form */
    accepts_overpayment || accepts_underpayment ? (
      <ButtonWithAction
        minWidth={0}
        variant="smallGhost"
        action={() => setIsEditing(true)}
        text={<PencilIcon ariaLabel="Edit Amount" />}
      />
    ) : (
      ''
    );

  const renderIsEditingAmountButtons = () => (
    <Box padding="0">
      <ButtonWithAction
        extraStyles={`min-width: 0; margin: 0.5rem 0;`}
        extraDisabledStyles={`background-color: transparent; color: ${constants.colors.TROUT_GREY}; &:hover { text-decoration: none }`}
        variant="smallGhost"
        action={() => {
          handleAmountUpdate(item.uniqueId, inlineAmount);
          setIsEditing(false);
        }}
        disabled={inlineAmountError}
        text="Submit"
        textExtraStyles="font-size: 1rem;"
      />
      <ButtonWithAction
        extraStyles={`min-width: 0; margin: 0.5rem 0;`}
        variant="smallGhost"
        action={() => {
          // reset to given item amount and initial non-error state
          setIsEditing(false);
          setInlineAmount(item.amount);
          setInlineAmountError(false);
        }}
        text="Cancel"
        textExtraStyles="font-size: 1rem;"
      />
    </Box>
  );

  const renderStaticAmountText = (item) => {
    const formattedAmount = `$${removeLeadingMinus(formatCurrency(item.amount))}`;
    return (
      <Text variant="p">
        {item.is_negative_amount || item.amount < 0
          ? formatWithParentheses(formattedAmount)
          : formattedAmount}
      </Text>
    );
  };

  const renderEditAmountInput = () => (
    <input
      data-testid="search-edit-amount-input"
      type="number"
      value={inlineAmount}
      onChange={(event) => {
        const amount = event.currentTarget.value;
        setInlineAmount(amount);
        isAmountValid(amount)
          ? setInlineAmountError(false)
          : setInlineAmountError(true);
      }}
    />
  );

  const renderAmountError = (amount) => (
    <Box data-testid={`search-item-amount-error`}>
      <Text color={constants.colors.THUNDERBIRD_RED}>
        ${amount} is not a valid amount
      </Text>
    </Box>
  );

  return (
    <SearchResultItemWrapper data-testid="search-result-row">
      <Box padding=".25rem">
        <Cluster justify="space-between" align="center" nowrap>
          <Box padding="8px 0px 0px 8px" data-testid="search-result-column">
            <Checkbox
              name={item.external_uid?.toString()}
              checked={checked}
              error={item.error}
              disabled={item.disabled}
              focused={item.focused}
              onChange={onCheckBoxClick}
            />
          </Box>
          {item.table_values.map((value, idx) => (
            <Box
              key={`${value}-${idx}`}
              padding="0 8px 0 0"
              width="115px"
              data-testid="search-result-column"
              extraStyles={'overflow-wrap: break-word; overflow: hidden;'}
            >
              {value}
            </Box>
          ))}
          {annotationFields.map((field) => (
            <Box
              key={field.name}
              padding="0 8px 0 0"
              width="200px"
              data-testid="search-result-column"
            >
              <AnnotationInput
                data-testid={`search-result-annotation-${field.name}`}
                type="text"
                placeholder={field.required ? 'Required' : 'Optional'}
                value={item.annotations?.[field.name] || ''}
                onChange={(e) =>
                  handleAnnotationUpdate(
                    item.uniqueId,
                    field.name,
                    e.target.value,
                  )
                }
                onBlur={(e) =>
                  handleAnnotationUpdate(
                    item.uniqueId,
                    field.name,
                    e.target.value.trim(),
                  )
                }
              />
            </Box>
          ))}
          <Box width="95px" padding="0" data-testid="search-result-column">
            {isEditing
              ? renderEditAmountInput(item)
              : renderStaticAmountText(item)}
          </Box>
          {(accepts_overpayment || accepts_underpayment) && (
            // If both accepts over/under payment are false, dont show the Edit Amount column at all because the user cant modify the data
            <Box padding="0" width="70px" data-testid="search-result-column">
              {isEditing
                ? renderIsEditingAmountButtons()
                : renderEditAmountButton()}
            </Box>
          )}
        </Cluster>
        <Cluster justify="flex-end" align="center">
          {inlineAmountError ? renderAmountError(inlineAmount) : noop()}
        </Cluster>
      </Box>
    </SearchResultItemWrapper>
  );
};

const SearchResultItemWrapper = styled.div`
  &:nth-child(even) {
    background: #eee;
  }

  input[type='number'] {
    width: 80px;
  }
`;

const AnnotationInput = styled.input`
  width: 100%;
  padding: 4px 8px;
  border: 1px solid ${constants.colors.TROUT_GREY};
  border-radius: 4px;
  font-size: 0.875rem;
  box-sizing: border-box;
`;
